import React from 'react';

const CHUNK_RELOAD_KEY = '__ibiz_chunk_reload_ts__';
const CHUNK_RELOAD_COOLDOWN_MS = 5 * 60 * 1000; // 5 分钟内最多强制刷新一次，防止死循环

/**
 * 判断当前是否允许因 chunk 加载失败而强制整页重载。
 * 使用 sessionStorage 记录上次触发时间，避免连续失败导致刷新风暴。
 */
function shouldForceReload(): boolean {
  try {
    const last = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!last) return true;
    return Date.now() - parseInt(last, 10) > CHUNK_RELOAD_COOLDOWN_MS;
  } catch {
    return true;
  }
}

function markForceReload(): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

/**
 * 强制整页重载并戳穿缓存：给 URL 加上时间戳，确保浏览器重新请求 index.html。
 * 使用 location.replace 避免把带 ?_t= 的 URL 写入历史记录。
 */
function hardReloadWithCacheBust(): void {
  const url = new URL(window.location.href);
  url.searchParams.set('_t', String(Date.now()));
  window.location.replace(url.toString());
}

function isChunkLoadError(err: unknown): boolean {
  const msg = String((err && (err as any).message) || '');
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('Loading chunk') ||
    msg.includes('NetworkError') ||
    msg.includes('network error')
  );
}

/**
 * 带重试的 React.lazy 包装器。
 *
 * 解决场景：蓝绿部署后，浏览器旧标签页仍引用上一版本的 JS chunk 文件名，
 * 刷新/切换路由时请求已删除的旧 chunk 会抛 "Failed to fetch dynamically imported module"。
 *
 * 策略：
 * 1. 对网络抖动类错误先重试 N 次；
 * 2. 若仍是 chunk 加载错误，且最近 5 分钟未强制刷新过，则自动带时间戳整页重载，
 *    让浏览器重新请求新的 index.html 与主包；
 * 3. 若已刷新过仍失败，则把错误抛给上层错误边界。
 */
export function lazyWithRetry<T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>,
  retries = 2,
  delayMs = 500
): React.LazyExoticComponent<React.ComponentType<any>> {
  return React.lazy(() => retryImport(factory, retries, delayMs));
}

async function retryImport<T>(
  factory: () => Promise<T>,
  retries: number,
  delayMs: number
): Promise<T> {
  try {
    return await factory();
  } catch (err) {
    const loadError = isChunkLoadError(err);

    if (loadError && retries <= 0 && shouldForceReload()) {
      markForceReload();
      hardReloadWithCacheBust();
      // 页面即将重载，此 Promise 永不 resolve 即可。
      return new Promise(() => {});
    }

    if (!loadError || retries <= 0) throw err;

    await new Promise((resolve) => setTimeout(resolve, delayMs));
    return retryImport(factory, retries - 1, delayMs * 1.5);
  }
}
